import { DataTable } from '../ui/DataTable';
import { Dropdown } from '../ui/Dropdown';
import { Edit2, UserX, UserCheck, Eye, User } from 'lucide-react';
import type { UserWithDetails } from '../../hooks/useUsers';

interface UserListProps {
  users: UserWithDetails[];
  currentUserId?: string;
  onEdit: (user: UserWithDetails) => void;
  onToggleStatus: (user: UserWithDetails) => void;
  onViewSessions: (user: UserWithDetails) => void;
  loading?: boolean;
}

export function UserList({ users, currentUserId, onEdit, onToggleStatus, onViewSessions, loading }: UserListProps) {
  const columns = [
    {
      header: 'Utilisateur',
      accessor: (user: UserWithDetails) => {
        const isCurrentUser = currentUserId && user.id === currentUserId;
        return (
          <div className="flex items-center">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
              isCurrentUser 
                ? 'bg-blue-500 text-white ring-2 ring-blue-300' 
                : 'bg-primary text-white'
            }`}>
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
              ) : (
                user.name?.charAt(0).toUpperCase() || <User className="w-4 h-4" />
              )}
            </div>
            <div className="ml-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">{user.name || 'Utilisateur'}</p>
                {isCurrentUser && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    Vous
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
        );
      },
      sortable: true,
      width: 250
    },
    {
      header: 'Service',
      accessor: (user: UserWithDetails) => user.department || 'Non défini',
      sortable: true,
      filterable: true
    },
    {
      header: 'Rôle',
      accessor: (user: UserWithDetails) => ({
        superadmin: 'Super Admin',
        admin: 'Administrateur',
        user: 'Utilisateur',
        auditor: 'Auditeur',
        comptable: 'Comptable',
        gérant: 'Gérant',
        portfoliomanager: 'Portfolio Manager',
        accountant: 'Comptable',
        viewer: 'Lecteur'
      }[user.role] || user.role || 'Rôle inconnu'),
      sortable: true,
      filterable: true
    },
    {
      header: 'Statut',
      accessor: (user: UserWithDetails) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          user.status === 'active' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : user.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {user.status === 'active' ? 'Actif' : user.status === 'pending' ? 'En attente' : 'Inactif'}
        </span>
      ),
      sortable: true
    },
    {
      header: 'Dernière connexion',
      accessor: (user: UserWithDetails) => {
        if (!user.lastLogin) return 'Jamais connecté';
        try {
          return new Date(user.lastLogin).toLocaleString('fr-FR');
        } catch {
          return user.lastLogin;
        }
      },
      sortable: true
    },
    {
      header: 'Actions',
      accessor: (user: UserWithDetails) => {
        const isCurrentUser = currentUserId && user.id === currentUserId;
        const actions = [
          {
            label: 'Modifier',
            icon: Edit2,
            onClick: () => onEdit(user),
            variant: 'primary' as const
          },
          {
            label: 'Sessions',
            icon: Eye,
            onClick: () => onViewSessions(user),
            variant: 'default' as const
          },
          {
            label: user.status === 'active' ? 'Désactiver' : 'Activer',
            icon: user.status === 'active' ? UserX : UserCheck,
            onClick: () => onToggleStatus(user),
            variant: user.status === 'active' ? ('danger' as const) : ('success' as const),
            // Ne pas permettre de désactiver un superadmin ou soi-même
            disabled: user.role === 'superadmin' || isCurrentUser,
            separator: true
          }
        ];

        return <Dropdown items={actions} align="right" />;
      },
      width: 80,
      sortable: false,
      hideable: false
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={users}
      loading={loading}
      emptyMessage="Aucun utilisateur trouvé"
      showToolbar={true}
      enableResize={true}
      enableSort={true}
      enableFilters={true}
      enableColumnToggle={true}
      enableGlobalFilter={true}
      showPagination={true}
      storageKey="user-list-table"
      density="normal"
    />
  );
}